/**
 * `GET /api/v1/me`'s answer, published as a CONTRACT so that no side can hold a
 * private opinion of its shape.
 *
 * **A48, booked round 184, and this file is the law's first application.** The
 * staging sitting of 29 August 2026: login answered `200`, the token was set,
 * and the buyer was returned to the landing screen with an empty console and
 * nothing thrown. `identityFrom` in `client-web` read `fullName` and `phone` at
 * the TOP LEVEL of this answer; the server has always nested them under
 * `profile`. **They had never once agreed**, so every real `/me` parsed to
 * `null` and `useSession.ts` silently decided it did not know who he was.
 *
 * **Twenty-two e2e assertions were green over it**, because `api-stub.ts`
 * answered `{ fullName, phone, city }` , the shape the client wanted rather
 * than the shape the server sends. *A stub written from the client's
 * expectation is the client testifying for itself.* That is PRECEDENTS section
 * 4 for the third time and the reason this interface is here rather than
 * duplicated at both ends: the controller returns it and the stub is typed by
 * it, so a stub that forgets `profile` fails to compile instead of failing a
 * founder.
 *
 * **A type is necessary and not sufficient**, which is why it does not travel
 * alone. `me-contract.e2e.spec.ts` stands up the REAL application and asserts
 * that `client-web`'s own parser reads a live answer , the only witness that
 * could have caught this, because a shared type still cannot notice that two
 * files agree with each other and with nothing that runs.
 */
export interface MeAnswer {
  principal: { id: string; kind: string };
  organisation: { id: string; name: string; type: string } | null;
  role: string | null;
  view: { viewId: string; landing: string } | null;
  screens: readonly string[];
  permissions: Record<string, string>;
  /**
   * `P0-DLW-006`: the roles this principal's own role may invite, from
   * `identity.roles.may_invite_roles`.
   *
   * **A screen must not know which role it may create.** I16 decides it, by
   * DATA, and `decideInvite` is the sentence that enforces it; a form that
   * hard-coded `employeConcession` would be the second place that decision
   * lives, and the day the founder re-cuts a dealership employee's rights the
   * two would disagree with no test between them. So the api says what the
   * caller may invite, and the form reads it.
   *
   * Empty for a principal who may invite nobody, which is most of them , and an
   * empty array is an answer, not an absence.
   */
  mayInviteRoles: readonly string[];
  /**
   * `P0-PLT-034`. 4.7.3 exposes the flags *"to the frontend through
   * `/api/v1/me`"*, already evaluated for this environment , a screen never
   * decides whether a flag applies here, because that decision is the one
   * `allowed_environments` exists to make.
   */
  flags: Record<string, boolean>;
  /**
   * **The nesting that cost a sitting.** It is here, under `profile`, and it has
   * been since `P0-IDN-012`. Anything that reads a name or a number reads it
   * from this object.
   */
  /**
   * `round CW`, `CW.7`: `city` is OPTIONAL on the wire and only a client's
   * answer carries it , a staff member's screen has no use for one, and a
   * field every answer must fill is a field somebody fills with a guess.
   */
  profile: { fullName: string; email: string | null; phone: string | null; city?: string };
}

/** What a client screen needs of `/me`, and all it needs. */
export interface ClientIdentity {
  readonly fullName: string;
  readonly phone: string;
  readonly city: string;
}

/**
 * **The ONE reader of this contract, and it lives with the contract.**
 *
 * The first attempt at round 184's witness put this function in `client-web`
 * and imported it from an API integration spec. The toolchain refused the edge
 * , `services/api`'s tsconfig sets no `jsx`, and the app's session module
 * reaches `@hakili/app-shell`, which is React , and **the refusal was the right
 * answer to the wrong design.** A function whose entire job is *read this
 * contract* does not belong to one consumer of it. It belongs here, beside the
 * shape it reads, where the client app, the API's contract witness and any
 * future reader all get the same one.
 *
 * `null` means *this is not an answer I can seat a person on*, and the caller
 * decides what that means. It is not an error: the boot path asks the same
 * question of a spent cookie.
 *
 * **`city` is READ now, and `round CW`, `CW.7` is what asked for it.** This
 * comment used to say the server had never sent one and that the honest blank
 * was what a screen should render , true then, and the reason the blank was
 * never faked. The pair of the search screen showed the gap: the drawing opens
 * every client screen with a bar naming the mark and the buyer's CITY, and the
 * column has held it since enrolment collected it. So `/me` projects it, and a
 * client with none recorded still answers the empty string , the blank stays
 * honest, and a screen that meets it shows nothing rather than a guess.
 */
export function clientIdentityFrom(answer: unknown): ClientIdentity | null {
  if (typeof answer !== "object" || answer === null) return null;
  const profile = (answer as { profile?: unknown }).profile;
  if (typeof profile !== "object" || profile === null) return null;
  const row = profile as Record<string, unknown>;
  const fullName = row["fullName"];
  const phone = row["phone"];
  if (typeof fullName !== "string" || typeof phone !== "string") return null;
  const city = row["city"];
  return { fullName, phone, city: typeof city === "string" ? city : "" };
}
